import { useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { useAuth } from "@/hooks/useAuth";

const CONTEXTUAL_MESSAGES = [
  "Noticed a bug?",
  "Got a suggestion?",
  "Something feel off?",
  "Have an idea?",
  "Want to improve this?",
  "Thoughts on this?",
  "Missing something?",
  "Found an issue?",
  "How can we improve?",
  "Tell us what you think",
  "Spot something weird?",
  "Feature request?",
  "Love it or hate it?",
  "Help us get better",
] as const;

interface InlineFeedbackProps {
  /** Index into the messages array for diverse text — or pass a custom label */
  variant?: number;
  /** Custom label overrides the variant-based message */
  label?: string;
  className?: string;
}

export const InlineFeedback = ({ variant = 0, label, className = "" }: InlineFeedbackProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const message = label ?? CONTEXTUAL_MESSAGES[variant % CONTEXTUAL_MESSAGES.length];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group relative inline-flex min-h-[48px] w-full max-w-full items-center justify-start gap-3 overflow-hidden rounded-xl border border-primary/25 px-4 py-2.5 text-left text-primary-foreground shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_hsl(var(--primary)/0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:w-auto ${className}`}
        style={{
          background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)), hsl(var(--accent)), hsl(var(--primary)))',
          backgroundSize: '300% 300%',
          animation: 'gradient-shift 8s ease-in-out infinite',
        }}
      >
        <span className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background/15 backdrop-blur-sm">
          <MessageSquarePlus className="h-4 w-4 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6" />
        </span>
        <span className="relative flex min-w-0 flex-col">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
            Send feedback
          </span>
          <span className="text-sm font-semibold leading-tight">{message}</span>
        </span>
      </button>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
